import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import { Search, X, CornerDownLeft, ChevronRight } from 'lucide-react';

// ─── FLATTEN ALL MENU ITEMS RECURSIVELY ──────────────────────────────────────
function flattenMenu(nodes, breadcrumb = []) {
  const result = [];
  for (const node of nodes || []) {
    if (node.type === 'item' && node.url) {
      result.push({
        id: node.id,
        title: node.title || '',
        url: node.url,
        icon: node.icon || null,
        breadcrumb: [...breadcrumb],
      });
    } else if ((node.type === 'collapse' || node.type === 'group') && node.children) {
      result.push(...flattenMenu(node.children, [...breadcrumb, node.title].filter(Boolean)));
    }
  }
  return result;
}

// ─── BUILD ABBREVIATION: "Chart of Accounts" → "coa" ────────────────────────
function buildAbbr(title) {
  return title
    .toLowerCase()
    .split(/\s+/)
    .map((w) => w[0])
    .join('');
}

// ─── FUZZY CHARACTER SEQUENCE CHECK ──────────────────────────────────────────
function fuzzyCharMatch(query, target) {
  let qi = 0;
  const t = target.toLowerCase();
  const q = query.toLowerCase();
  for (let i = 0; i < t.length && qi < q.length; i++) {
    if (t[i] === q[qi]) qi++;
  }
  return qi === q.length;
}

// ─── SCORE: higher = better match ────────────────────────────────────────────
function scoreMatch(query, item) {
  const q = query.toLowerCase().trim();
  const title = item.title.toLowerCase();
  const abbr = buildAbbr(item.title);
  const breadcrumbStr = item.breadcrumb.join(' ').toLowerCase();

  if (title === q) return 100;
  if (abbr === q) return 95;
  if (title.startsWith(q)) return 90;
  if (abbr.startsWith(q)) return 85;
  if (title.includes(q)) return 80;
  if (title.split(/\s+/).some((w) => w.startsWith(q))) return 72;
  if (title.split(/\s+/).some((w) => w.includes(q))) return 64;
  if (breadcrumbStr.includes(q)) return 55;
  if (fuzzyCharMatch(q, title)) return 45;
  if (fuzzyCharMatch(q, breadcrumbStr)) return 35;
  return 0;
}

// ─── HIGHLIGHT: wrap matched chars with <mark> ────────────────────────────────
function HighlightedText({ text, query }) {
  if (!query) return <span>{text}</span>;
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  const idx = t.indexOf(q);

  if (idx !== -1) {
    return (
      <span>
        {text.slice(0, idx)}
        <mark style={{ background: 'rgba(99,102,241,0.18)', color: '#4f46e5', borderRadius: 2, padding: '0 1px' }}>
          {text.slice(idx, idx + q.length)}
        </mark>
        {text.slice(idx + q.length)}
      </span>
    );
  }

  // Fuzzy highlight: highlight matched characters in sequence
  const qChars = q.split('');
  let qi = 0;
  const parts = [];
  for (let i = 0; i < text.length; i++) {
    if (qi < qChars.length && text[i].toLowerCase() === qChars[qi]) {
      parts.push(
        <mark key={i} style={{ background: 'rgba(99,102,241,0.18)', color: '#4f46e5', borderRadius: 2, padding: '0 1px' }}>
          {text[i]}
        </mark>
      );
      qi++;
    } else {
      parts.push(<span key={i}>{text[i]}</span>);
    }
  }
  return <span>{parts}</span>;
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function SidebarSearch({ menuData, drawerOpen }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const itemRefs = useRef([]);

  // Flatten once, memoized
  const allItems = useMemo(() => {
    const groups = Array.isArray(menuData?.items)
      ? menuData.items
      : Array.isArray(menuData?.children)
        ? [menuData]
        : [];

    const nodes = groups.flatMap((group) => group.children || group.items || []);
    return flattenMenu(nodes);
  }, [menuData]);

  // Filter + sort by score — runs synchronously, no lag
  const results = useMemo(() => {
    if (!query.trim()) return [];
    return allItems
      .map((item) => ({ item, score: scoreMatch(query, item) }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8)
      .map(({ item }) => item);
  }, [query, allItems]);

  // Reset active index when results change
  useEffect(() => {
    setActiveIdx(0);
  }, [results]);

  // Scroll active item into view
  useEffect(() => {
    if (itemRefs.current[activeIdx]) {
      itemRefs.current[activeIdx].scrollIntoView({ block: 'nearest' });
    }
  }, [activeIdx]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      if (!results.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (results[activeIdx]) {
          navigate(results[activeIdx].url);
          setQuery('');
          inputRef.current?.blur();
        }
      } else if (e.key === 'Escape') {
        setQuery('');
        inputRef.current?.blur();
      }
    },
    [results, activeIdx, navigate]
  );

  const handleSelect = (url) => {
    navigate(url);
    setQuery('');
    setIsFocused(false);
  };

  const showDropdown = isFocused && query.trim().length > 0;

  // If drawer is closed, show only icon
  if (!drawerOpen) {
    return (
      <Box
        onClick={() => inputRef.current?.focus()}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 1.5,
          cursor: 'pointer',
          color: '#A0A5AE',
          '&:hover': { bgcolor: 'secondary.lighter', color: 'primary.main' },
          transition: 'all 0.15s',
        }}
      >
        <Search size={20} strokeWidth={1.5} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 1.5, pb: .5, pt: 2, position: 'relative' }}>
      {/* Input wrapper */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          py: 1.2,
          borderRadius: '10px',
          border: '1.5px solid',
          borderColor: isFocused ? 'primary.main' : 'divider',
          bgcolor: isFocused ? 'primary.lighter' : 'action.hover',
          transition: 'all 0.18s ease',
          cursor: 'text',
        }}
        onClick={() => inputRef.current?.focus()}
      >
        <Search
          size={15}
          strokeWidth={2}
          style={{ color: isFocused ? '#4f46e5' : '#A0A5AE', flexShrink: 0, transition: 'color 0.18s' }}
        />
        <InputBase
          inputRef={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => setIsFocused(false), 150)}
          onKeyDown={handleKeyDown}
          placeholder="Search menu..."
          sx={{
            flex: 1,
            fontSize: '12.5px',
            fontFamily: '"Sofia Sans", sans-serif',
            color: 'text.primary',
            '& input': { p: 0, '&::placeholder': { color: '#A0A5AE', opacity: 1 } },
          }}
        />
        {query && (
          <Box
            onClick={(e) => { e.stopPropagation(); setQuery(''); inputRef.current?.focus(); }}
            sx={{ cursor: 'pointer', display: 'flex', color: '#A0A5AE', '&:hover': { color: 'text.primary' } }}
          >
            <X size={13} strokeWidth={2} />
          </Box>
        )}
      </Box>

      {/* Dropdown results */}
      {showDropdown && (
        <Box
          ref={listRef}
          sx={{
            position: 'absolute',
            top: 'calc(100% - 4px)',
            left: 12,
            right: 12,
            zIndex: 9999,
            bgcolor: 'background.paper',
            border: '1.5px solid',
            borderColor: 'divider',
            borderRadius: '10px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            overflow: 'hidden',
            maxHeight: '320px',
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 },
          }}
        >
          {results.length === 0 ? (
            <Box sx={{ px: 2, py: 2.5, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '12px', color: '#A0A5AE', fontFamily: '"Sofia Sans", sans-serif' }}>
                No results for &quot;{query}&quot;
              </Typography>
            </Box>
          ) : (
            <>
              {results.map((item, idx) => {
                const Icon = item.icon;
                const isActive = idx === activeIdx;
                return (
                  <Box
                    key={item.id}
                    ref={(el) => (itemRefs.current[idx] = el)}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onMouseDown={() => handleSelect(item.url)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      px: 1.5,
                      py: 1,
                      cursor: 'pointer',
                      bgcolor: isActive ? 'primary.lighter' : 'transparent',
                      borderLeft: '2px solid',
                      borderColor: isActive ? 'primary.main' : 'transparent',
                      transition: 'all 0.1s',
                    }}
                  >
                    {/* Icon */}
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: isActive ? 'primary.main' : 'action.hover',
                        flexShrink: 0,
                        transition: 'all 0.1s',
                      }}
                    >
                      {Icon ? (
                        <Icon size={14} strokeWidth={1.5} style={{ color: isActive ? '#fff' : '#A0A5AE' }} />
                      ) : (
                        <ChevronRight size={14} strokeWidth={1.5} style={{ color: isActive ? '#fff' : '#A0A5AE' }} />
                      )}
                    </Box>

                    {/* Text */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: '12.5px',
                          fontWeight: 600,
                          color: isActive ? 'primary.main' : 'text.primary',
                          fontFamily: '"Sofia Sans", sans-serif',
                          lineHeight: 1.3,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        <HighlightedText text={item.title} query={query} />
                      </Typography>
                      {item.breadcrumb.length > 0 && (
                        <Typography
                          sx={{
                            fontSize: '10px',
                            color: '#A0A5AE',
                            fontFamily: '"Sofia Sans", sans-serif',
                            mt: 0.2,
                          }}
                        >
                          {item.breadcrumb.join(' › ')}
                        </Typography>
                      )}
                    </Box>

                    {/* Enter hint on active */}
                    {isActive && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        <CornerDownLeft size={11} style={{ color: '#4f46e5' }} />
                      </Box>
                    )}
                  </Box>
                );
              })}

              {/* Footer hint */}
              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'center',
                }}
              >
                {[
                  { icon: <CornerDownLeft size={10} />, label: 'Select' },
                  { icon: <span style={{ fontSize: 10 }}>↑↓</span>, label: 'Navigate' },
                  { icon: <span style={{ fontSize: 10 }}>Esc</span>, label: 'Close' },
                ].map(({ icon, label }) => (
                  <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'action.hover',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '4px',
                        px: 0.5,
                        minWidth: 18,
                        height: 16,
                        color: '#A0A5AE',
                      }}
                    >
                      {icon}
                    </Box>
                    <Typography sx={{ fontSize: '10px', color: '#A0A5AE', fontFamily: '"Sofia Sans", sans-serif' }}>
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}